import { useCallback, useMemo, useState } from 'react'
import { useShopRouteState } from './useShopRouteState'
import { useShopRouteContent } from './useShopRouteContent'
import { useShopHomeRuntime } from './useShopHomeRuntime'
import { useShopFieldDefinitions } from './useShopFieldDefinitions'
import { useShopThemeRuntime } from './useShopThemeRuntime'
import type { AppProps, CatalogProduct } from '../types'
import {
  buildProductGallery,
  hasHomeContentBlocks,
  resolveActiveProductImage,
  resolveActiveRouteError,
} from './useShopAppRuntime.helpers'

export const useShopAppRuntime = ({
  initialPathname = '/',
  initialSearch = '',
  initialLocale,
}: AppProps) => {
  useShopThemeRuntime()
  const { route, navigateTo } = useShopRouteState({ initialPathname, initialSearch, initialLocale })
  const [selectedProductImage, setSelectedProductImage] = useState<string>('')

  const handleResolvedProduct = useCallback((product: CatalogProduct) => {
    setSelectedProductImage(product.mediaGallery?.[0] || '')
  }, [])

  const {
    pageData,
    pageLoading,
    pageError,
    productData,
    productLoading,
    productError,
    categoryRouteData,
  } = useShopRouteContent({
    route,
    onProductResolved: handleResolvedProduct,
  })

  const {
    isAdmin,
    slides,
    currentSlide,
    currentIndex,
    setCurrentIndex,
    goNext,
    goPrev,
    status,
    homeSettings,
    homePageData,
    homePageLoading,
    homePageError,
  } = useShopHomeRuntime({ routeMode: route.mode })

  const { productFieldDefinitions } = useShopFieldDefinitions()

  const productGallery = useMemo(() => buildProductGallery(productData), [productData])

  const activeProductImage = resolveActiveProductImage({
    productGallery,
    selectedProductImage,
  })

  const activeError = useMemo(
    () =>
      resolveActiveRouteError({
        route,
        productLoading,
        productError,
        pageLoading,
        pageError,
      }),
    [pageError, pageLoading, productError, productLoading, route],
  )

  const hasHomeBlocks = hasHomeContentBlocks(homePageData)

  return {
    route,
    navigateTo,
    pageData,
    pageLoading,
    pageError,
    productData,
    productLoading,
    productError,
    categoryRouteData,
    isAdmin,
    slides,
    currentSlide,
    currentIndex,
    status,
    homeSettings,
    homePageData,
    homePageLoading,
    homePageError,
    productFieldDefinitions,
    productGallery,
    activeProductImage,
    setSelectedProductImage,
    setCurrentIndex,
    goNext,
    goPrev,
    activeError,
    hasHomeBlocks,
  }
}
